/**
 * A shoot day, and whether it is still free.
 *
 * ── WHAT THIS REPLACED, AND WHY IT GOT SMALLER ───────────────────────────
 * This file used to hold a three-state machine: a day was untouched, or half
 * sold, or spoken for, because spec §12 sold it as „1 cały plan (8h) ALBO do 2
 * półplanów (3h plus 3h)". The owner has retired that split — *„dotychczasowy
 * podział Cały plan i Pół planu nie jest już używany"* — so the asymmetry the
 * machine existed to model is gone with it.
 *
 * What is left is the truth the split was wrapped around: ONE person can do
 * ONE shoot on ONE day. A photographer books a session that day; an operator
 * records a kind of material that day. Neither is sold by the hour any more,
 * and the day is not divisible.
 *
 * The WORK is now named by the thing being made, not by the length of the
 * booking: `shoot_rate.session` for the photographer, `recording_type` for the
 * operator. This module has no opinion about either — it only knows that the
 * day is taken or it is not.
 *
 * It lives in `@repo/db` rather than in `@repo/core` for the reason
 * `rental-terms.ts` does: the checkout TRANSACTION has to ask it, from inside
 * the transaction, and `@repo/db` may not import `@repo/core`.
 *
 * Pure and dependency-free.
 */

/**
 * How long a shoot day is, in hours.
 *
 * Carried over unchanged from the full plan it replaced — it was the day's
 * length before the split existed and it is the day's length now. It is what
 * the catalogue stores as the level's `durationMinutes`, so a calendar entry
 * has a width.
 */
export const SHOOT_DAY_HOURS = 8

/** The variant slug the PHOTOGRAPHER's day is sold under. One length, 8 h. */
export const SHOOT_DAY_SLUG = 'dzien-zdjeciowy'

/**
 * The OPERATOR's day comes in two lengths again (właściciel, 2026-10-12).
 *
 * ── THIS IS NOT THE OLD SPLIT COMING BACK ────────────────────────────────
 * The retired one was a rule the SYSTEM enforced: a day was „1 cały plan (8h)
 * ALBO do 2 półplanów", and most of this module was the machine that kept the
 * two shapes from overlapping. What comes back is only the two lengths. Asked
 * whether an operator may take two half plans in one day, the owner said:
 * *„Tak, ale musi to zadeklarować w panelu, to jego decyzja czy tak chce
 * zrobić"*.
 *
 * That lands on machinery that already exists and needs no migration:
 * `capacity_day` is keyed by (person, VARIANT, day) and carries a declared
 * `capacity`. „Two half plans on Thursday" is `capacity = 2` on this slug for
 * that Thursday — a number the operator types, not a shape the code infers.
 * One booking still costs one unit of the level it was sold as, so
 * {@link DAY_UNITS} stays at one.
 *
 * It also means the two levels do not police each other: an operator who
 * declares a full plan AND two half plans on one day has declared eighteen
 * hours of work. That is the owner's model — the declaration binds, and it is
 * his to make — not an oversight here.
 */
export const HALF_PLAN_SLUG = 'pol-planu'
export const FULL_PLAN_SLUG = 'caly-plan'

/** How long each level runs, in hours. The photographer's day is the default. */
const HOURS_BY_SLUG: Readonly<Record<string, number>> = {
  [SHOOT_DAY_SLUG]: SHOOT_DAY_HOURS,
  [HALF_PLAN_SLUG]: 5,
  [FULL_PLAN_SLUG]: 8,
}

/**
 * How long THIS level runs. Falls back to the photographer's eight hours for
 * anything unrecognised, which is what every caller assumed before the
 * operator had two of them — a wrong-but-familiar window beats a crash in the
 * middle of a booking.
 */
export function shootDayHours(slug: string | null | undefined): number {
  return (slug ? HOURS_BY_SLUG[slug] : undefined) ?? SHOOT_DAY_HOURS
}

/**
 * What one day holds, in the units `capacity_day.capacity` counts.
 *
 * ONE, where it used to be two half-plan units. The column keeps counting
 * units rather than days so that a shoot sits in `capacity_day` beside every
 * other kind of work and is sold the same atomic way — `sold_count <= capacity`
 * enforced by the DATABASE — but a shoot day now costs exactly one of them.
 */
export const DAY_UNITS = 1

/**
 * Is this variant slug a shoot day at all?
 *
 * A SET now rather than one comparison, and that is the load-bearing edit of
 * this change: `capacity.service.ts`, `orders/mappers.ts` and the creator all
 * decide „is this a day booking?" through here, so an operator level missing
 * from this list would be priced and sold while the calendar quietly refused
 * to see it.
 */
const SHOOT_DAY_SLUGS: ReadonlySet<string> = new Set([
  SHOOT_DAY_SLUG,
  HALF_PLAN_SLUG,
  FULL_PLAN_SLUG,
])

export function isShootDayLevel(slug: string | null | undefined): boolean {
  return slug !== null && slug !== undefined && SHOOT_DAY_SLUGS.has(slug)
}

/** How the level reads on a calendar entry and in a basket line. */
export function shootDayLabel(slug?: string | null): string {
  const hours = shootDayHours(slug)
  if (slug === HALF_PLAN_SLUG) return `Pół planu (${hours} h)`
  if (slug === FULL_PLAN_SLUG) return `Cały plan (${hours} h)`
  return `Dzień zdjęciowy (${hours} h)`
}

/** What one person's one day already holds. */
export interface DayLoad {
  /** How many shoots are booked on it. One is already too many for a second. */
  readonly bookedCount: number
}

export const EMPTY_DAY: DayLoad = { bookedCount: 0 }

/** Can another shoot still be sold onto this day? */
export function canSell(load: DayLoad): boolean {
  return load.bookedCount < DAY_UNITS
}

/** The day after one more booking lands on it. */
export function withBooking(load: DayLoad): DayLoad {
  return { bookedCount: load.bookedCount + 1 }
}

/**
 * Read a day's shape off what is booked on it.
 *
 * Derived rather than stored, and that is deliberate: a `capacity_day` row
 * disagreeing with the bookings on the same day is a number somebody has to
 * reconcile, and reconciling it is exactly the bug this file exists to
 * prevent. The bookings ARE the truth.
 */
export function loadFrom(bookings: readonly unknown[]): DayLoad {
  return { bookedCount: bookings.length }
}

/** Why this day cannot be sold — or `null` when it can. */
export function dayProblem(load: DayLoad): string | null {
  return canSell(load) ? null : 'Ten dzień jest już zajęty — wybierz inny termin.'
}

/** The same verdict, straight from the rows the query returns. */
export function shootDayProblem(bookings: readonly unknown[]): string | null {
  return dayProblem(loadFrom(bookings))
}

// ── AT WHAT HOUR ───────────────────────────────────────────────────────────

/**
 * The hours a shoot day may start at.
 *
 * ── WHY THEY LIVE HERE ───────────────────────────────────────────────────
 * They were a constant inside `production-page.client.tsx`, where only the
 * marketing page could see them. The shoot day CARRIES its start hour now, so
 * the creator validates it, the summary prints it, `refFrom` turns it into a
 * window and the availability check measures a photographer's declared hours
 * against it. Five callers, one list.
 *
 * ── AND WHY THERE IS ONE LIST ────────────────────────────────────────────
 * There were two, one per plan: a three-hour half could still start at 15:00,
 * a whole day could not. With the halves retired only the day's own list is
 * left — eight hours from the latest of these still ends inside the evening,
 * which is the rule the owner's list encodes.
 */
const DAY_STARTS: readonly string[] = ['08:00', '09:00', '10:00', '11:00']

export function dayStarts(): readonly string[] {
  return DAY_STARTS
}

/** `"09:00"` to `540` — a clock reading as minutes from midnight, or `null`. */
export function startMinute(clock: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(clock)
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null
  return hours * 60 + minutes
}
